import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Interface para los datos de creación de factura manual
 */
export interface CreateManualInvoiceData {
  team_id: number;
  erp_number: string;
  date: string; // formato YYYY-MM-DD
  base_total: number;
  taxes_total: number;
  grand_total: number;
  pdf_file?: File;
  expired_at?: string; // formato YYYY-MM-DD, opcional
}

/**
 * Respuesta de la API al crear factura manual
 */
export interface CreateManualInvoiceResponse {
  id: number;
  team_id: number;
  shop_id: null;
  erp_number: string;
  erp_id: null;
  date: string;
  period_month: string;
  reference_invoice_id: null;
  base_total: number;
  taxes_total: number;
  grand_total: number;
  pdf_url: string | null;
  xml_url: null;
  shipments: any[];
  extra_data: {
    have_compensation: boolean;
    is_wallet_discounted: boolean;
  };
  expired_at: string | null;
  type: 'MANUAL';
  preinvoice_id: null;
}

/**
 * useCreateManualInvoice - Hook para crear facturas manuales
 *
 * Ejecuta POST /invoices/manual con los datos de la factura manual.
 * Maneja estados de loading, éxito y error con toasts apropiados.
 *
 * @returns {Object} Función createManualInvoice y estado isCreating
 */
export const useCreateManualInvoice = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const createManualInvoice = async (
    data: CreateManualInvoiceData,
    onSuccess?: (response: CreateManualInvoiceResponse) => void,
  ): Promise<CreateManualInvoiceResponse | null> => {
    setIsCreating(true);

    try {
      console.log('🔍 useCreateManualInvoice - Creando factura manual:', data);

      const httpClient = createHttpClient(apiClientWH);

      // Crear FormData para enviar archivo y datos
      const formData = new FormData();
      formData.append('team_id', data.team_id.toString());
      formData.append('erp_number', data.erp_number);
      formData.append('date', data.date);
      formData.append('base_total', data.base_total.toString());
      formData.append('taxes_total', data.taxes_total.toString());
      formData.append('grand_total', data.grand_total.toString());

      if (data.expired_at) {
        formData.append('expired_at', data.expired_at);
      }

      if (data.pdf_file) {
        formData.append('pdf_file', data.pdf_file);
      }

      const response = await httpClient.post<CreateManualInvoiceResponse>('/invoices/manual', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('🔍 useCreateManualInvoice - Respuesta:', response);

      // Mostrar toast de éxito
      toast({
        title: t('toast.create_manual_invoice_success.title', 'Factura manual creada'),
        description: response?.erp_number
          ? t('toast.create_manual_invoice_success.description', {
              erpNumber: response.erp_number,
            })
          : t('toast.create_manual_invoice_success.description_generic', 'Factura manual creada exitosamente'),
        variant: 'default',
      });

      // Ejecutar callback de éxito
      onSuccess?.(response);

      return response;
    } catch (error: any) {
      console.log('🔍 useCreateManualInvoice - Error:', error);

      // Manejar errores específicos de validación
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0] as string[];

        toast({
          title: t('toast.create_manual_invoice_error.title', 'Error de validación'),
          description: firstError[0] || t('toast.create_manual_invoice_error.validation', 'Datos inválidos'),
          variant: 'destructive',
        });
      } else if (error?.response?.status === 400) {
        toast({
          title: t('toast.create_manual_invoice_error.title', 'Error al crear factura'),
          description:
            error?.response?.data?.message || t('toast.create_manual_invoice_error.duplicate', 'Número ERP ya existe'),
          variant: 'destructive',
        });
      } else {
        // Error genérico
        toast({
          title: t('toast.create_manual_invoice_error.title', 'Error al crear factura'),
          description:
            error?.message ||
            t(
              'toast.create_manual_invoice_error.unexpected',
              'Error inesperado al crear la factura. Inténtalo de nuevo.',
            ),
          variant: 'destructive',
        });
      }

      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createManualInvoice,
    isCreating,
  };
};
